import type { PontoRealizacao } from '../../../../../components/Charts/barrasEmpilhadasOpcoes'
import type { PontoSerie } from '../../../../../components/Charts/serieDiariaOpcoes'
import { IndicadorPerformance, PontoSerieDiaria } from './_models'
import { contagemDoDia } from './performance'

export type Agrupamento = 'dia' | 'semana'

const DIA_MS = 24 * 60 * 60 * 1000
const LIMITE_DIAS_AGRUPAMENTO_DIARIO = 31

// LocalDate como dia UTC: sem `new Date('aaaa-mm-dd')`, que desloca o dia no fuso do navegador.
const diaUtc = (data: string): number => {
  const [ano, mes, dia] = data.split('-').map(Number)
  return Date.UTC(ano, mes - 1, dia)
}

/** Dias do período, contando o primeiro e o último. */
export const diasNoPeriodo = (dataInicio: string, dataFim: string): number =>
  Math.round((diaUtc(dataFim) - diaUtc(dataInicio)) / DIA_MS) + 1

export const agrupamentoPadrao = (dataInicio: string, dataFim: string): Agrupamento =>
  diasNoPeriodo(dataInicio, dataFim) > LIMITE_DIAS_AGRUPAMENTO_DIARIO ? 'semana' : 'dia'

/** Segunda-feira da semana do dia, como chave. */
const segundaDaSemana = (data: string): number => {
  const dia = diaUtc(data)
  const desdeSegunda = (new Date(dia).getUTCDay() + 6) % 7
  return dia - desdeSegunda * DIA_MS
}

/** Pontos consecutivos da mesma semana (segunda a domingo), já recortada pelo período. */
const semanas = <T extends { data: string }>(pontos: T[]): T[][] => {
  const grupos: T[][] = []
  let chaveAtual: number | null = null
  pontos.forEach((ponto) => {
    const chave = segundaDaSemana(ponto.data)
    if (chave !== chaveAtual) {
      grupos.push([])
      chaveAtual = chave
    }
    grupos[grupos.length - 1].push(ponto)
  })
  return grupos
}

const intervalo = (grupo: { data: string }[]) => ({
  data: grupo[0].data,
  dataFim: grupo[grupo.length - 1].data,
})

/** Tempos: média semanal ponderada pelo `n` de cada dia, `Σ(valor·n)/Σn`. */
export const agruparMedia = (serie: PontoSerieDiaria[], agrupamento: Agrupamento): PontoSerie[] => {
  if (agrupamento === 'dia') return serie

  return semanas(serie).map((grupo) => {
    let somaN = 0
    let somaVN = 0
    grupo.forEach((ponto) => {
      if (ponto.valor === null || ponto.n <= 0) return
      somaN += ponto.n
      somaVN += ponto.valor * ponto.n
    })
    return { ...intervalo(grupo), n: somaN, valor: somaN > 0 ? somaVN / somaN : null }
  })
}

/** Taxas: contagens diárias somadas sobre o denominador da semana, `Σx/Σn·100`. */
export const agruparTaxa = (serie: PontoSerieDiaria[], agrupamento: Agrupamento): PontoSerie[] => {
  if (agrupamento === 'dia') return serie

  return semanas(serie).map((grupo) => {
    let somaN = 0
    let somaX = 0
    grupo.forEach((ponto) => {
      const x = contagemDoDia(ponto)
      if (x === null) return
      somaN += ponto.n
      somaX += x
    })
    return { ...intervalo(grupo), n: somaN, valor: somaN > 0 ? (somaX * 100) / somaN : null }
  })
}

export const agruparSerie = (
  indicador: IndicadorPerformance,
  agrupamento: Agrupamento
): PontoSerie[] | null => {
  if (!indicador.serie) return null
  return indicador.unidade === 'MINUTOS'
    ? agruparMedia(indicador.serie, agrupamento)
    : agruparTaxa(indicador.serie, agrupamento)
}

const lacuna = (data: string): PontoRealizacao => ({
  data,
  n: 0,
  realizados: null,
  cancelados: null,
  faltas: null,
})

/**
 * Realizados, cancelados e faltas, em contagem, a partir das taxas de cancelamento e de
 * absenteísmo (as duas têm os lançados como denominador). Dia em que as séries não
 * fecham vira lacuna, nunca número inventado.
 */
export const serieRealizacao = (
  cancelamento: IndicadorPerformance,
  absenteismo: IndicadorPerformance,
  agrupamento: Agrupamento
): PontoRealizacao[] => {
  const diasCancelamento = cancelamento.serie ?? []
  const diasAbsenteismo = absenteismo.serie ?? []

  const diarios = diasCancelamento.map((dia, i): PontoRealizacao => {
    const par = diasAbsenteismo[i]
    if (!par || par.data !== dia.data || par.n !== dia.n || dia.n <= 0) return lacuna(dia.data)

    const cancelados = contagemDoDia(dia)
    const faltas = contagemDoDia(par)
    if (cancelados === null || faltas === null) return lacuna(dia.data)

    const realizados = dia.n - cancelados - faltas
    if (realizados < 0) return lacuna(dia.data)

    return { data: dia.data, n: dia.n, realizados, cancelados, faltas }
  })

  if (agrupamento === 'dia') return diarios

  return semanas(diarios).map((grupo) => {
    const validos = grupo.filter((ponto) => ponto.n > 0 && ponto.realizados !== null)
    if (validos.length === 0) return { ...lacuna(grupo[0].data), dataFim: intervalo(grupo).dataFim }

    const soma = (campo: 'realizados' | 'cancelados' | 'faltas') =>
      validos.reduce((total, ponto) => total + (ponto[campo] ?? 0), 0)

    return {
      ...intervalo(grupo),
      n: validos.reduce((total, ponto) => total + ponto.n, 0),
      realizados: soma('realizados'),
      cancelados: soma('cancelados'),
      faltas: soma('faltas'),
    }
  })
}
